import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchPatterns } from '@/lib/api';
import type { ApiPattern } from '@/lib/api';
import { PatternThumbnail } from '@/components/editor/pattern-thumbnail';

const CATEGORIES = [
  { value: '', label: 'Wszystkie' },
  { value: 'geometric', label: 'Geometryczne' },
  { value: 'ornament', label: 'Ornamenty' },
  { value: 'letters', label: 'Litery' },
  { value: 'seasonal', label: 'Sezonowe' },
] as const;

const TECHNIQUES = [
  { value: '', label: 'Wszystkie' },
  { value: 'loom', label: 'Loom' },
  { value: 'peyote', label: 'Peyote' },
  { value: 'brick', label: 'Brick' },
] as const;

const DIFFICULTY_LABELS: Record<string, string> = {
  easy: 'Łatwy',
  medium: 'Średni',
  hard: 'Trudny',
};

const CATEGORY_LABELS: Record<string, string> = {
  geometric: 'Geometryczne',
  ornament: 'Ornamenty',
  letters: 'Litery',
  seasonal: 'Sezonowe',
};

export function HomePage() {
  const [category, setCategory] = useState('');
  const [technique, setTechnique] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['patterns', { category, technique }],
    queryFn: () =>
      fetchPatterns({
        ...(category ? { category } : {}),
        ...(technique ? { technique } : {}),
        limit: 100,
      }),
  });

  const patterns = data?.data ?? [];

  return (
    <div className="px-4 py-6">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Biblioteka wzorów</h1>
          <p className="text-sm text-gray-500">Gotowe wzory do edycji i inspiracji</p>
        </div>
        <Link
          to="/editor/new"
          className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          + Nowy
        </Link>
      </div>

      {/* Category filter */}
      <div className="mb-2 flex gap-1.5 overflow-x-auto">
        {CATEGORIES.map((c) => (
          <button
            key={c.value}
            onClick={() => setCategory(c.value)}
            className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              category === c.value
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Technique filter */}
      <div className="mb-4 flex gap-1.5 overflow-x-auto">
        {TECHNIQUES.map((t) => (
          <button
            key={t.value}
            onClick={() => setTechnique(t.value)}
            className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              technique === t.value
                ? 'bg-gray-700 text-white'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {isLoading && <p className="py-12 text-center text-sm text-gray-400">Ładuję wzory...</p>}

      {/* Grid */}
      {!isLoading && patterns.length === 0 && (
        <p className="py-12 text-center text-sm text-gray-400">Brak wzorów w tej kategorii</p>
      )}

      {patterns.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {patterns.map((pattern) => (
            <PatternCard key={pattern.id} pattern={pattern} />
          ))}
        </div>
      )}
    </div>
  );
}

function PatternCard({ pattern }: { pattern: ApiPattern }) {
  return (
    <Link
      to={`/editor/pattern/${pattern.slug}`}
      className="group overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
    >
      {/* Thumbnail */}
      <div className="flex h-24 items-center justify-center bg-gray-50">
        <PatternThumbnail slug={pattern.slug} width={120} height={96} />
      </div>

      {/* Info */}
      <div className="px-2.5 py-2">
        <h3 className="truncate text-sm font-medium text-gray-800">{pattern.name}</h3>
        <div className="mt-0.5 flex items-center gap-1.5">
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
            {pattern.technique}
          </span>
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
            {pattern.width}×{pattern.height}
          </span>
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
            {DIFFICULTY_LABELS[pattern.difficulty] ?? pattern.difficulty}
          </span>
        </div>
        <p className="mt-0.5 text-[10px] text-gray-400">
          {CATEGORY_LABELS[pattern.category] ?? pattern.category}
        </p>
      </div>
    </Link>
  );
}
